// frontend/src/components/admin/Activite.jsx
// ============================================================================
// ACTIVITE PAR CLIENT
// ----------------------------------------------------------------------------
// Qui se sert vraiment d'HoraPilot, et a quelle hauteur. Un signal par client :
// coupe, jamais entre, decroche, deborde, ou normal — pour reperer d'un coup
// d'oeil celui qu'il faut appeler cette semaine.
// ============================================================================

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  LineChart, RefreshCw, Ban, UserX, TrendingDown, AlertTriangle,
  CheckCircle2, Users, Clock, LogIn
} from 'lucide-react';
import superAdminApi from '../../services/superAdminApi';
import { useTheme } from '../../contexts/ThemeContext';

const FUSEAU = 'Africa/Brazzaville';

const SIGNAUX = {
  coupe:        { libelle: 'Accès coupé',      Icon: Ban,          ton: 'danger' },
  jamais_entre: { libelle: 'Jamais entré',     Icon: UserX,        ton: 'warning' },
  decroche:     { libelle: 'Décroche',         Icon: TrendingDown, ton: 'warning' },
  deborde:      { libelle: 'Dépasse son plafond', Icon: AlertTriangle, ton: 'warning' },
  normal:       { libelle: 'Actif',            Icon: CheckCircle2, ton: 'succes' }
};

const FENETRES = [
  { valeur: 7, libelle: '7 jours' },
  { valeur: 30, libelle: '30 jours' },
  { valeur: 90, libelle: '90 jours' }
];

const nombre = (n) => (Number.isFinite(Number(n)) ? Number(n).toLocaleString('fr-FR') : '—');

const quand = (iso) => {
  if (!iso) return 'jamais';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat('fr-FR', {
    timeZone: FUSEAU, day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'
  }).format(d);
};

const jourSeul = (valeur) => {
  if (!valeur) return 'jamais';
  const d = new Date(valeur);
  if (Number.isNaN(d.getTime())) return String(valeur).slice(0, 10);
  return new Intl.DateTimeFormat('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' }).format(d);
};

const Pastille = ({ signal, t }) => {
  const s = SIGNAUX[signal] || SIGNAUX.normal;
  const c = {
    danger:  { fond: t.dangerSoft,  texte: t.dangerText },
    warning: { fond: t.warningSoft, texte: t.warningText },
    succes:  { fond: t.successSoft, texte: t.successText }
  }[s.ton];
  const Icon = s.Icon;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px',
      borderRadius: 99, fontSize: 12, fontWeight: 700, background: c.fond, color: c.texte,
      whiteSpace: 'nowrap' }}>
      <Icon size={12} />
      {s.libelle}
    </span>
  );
};

const Jauge = ({ valeur, plafond, t }) => {
  const part = plafond ? Math.min((valeur / plafond) * 100, 100) : 0;
  const serre = plafond && valeur / plafond >= 0.9;
  return (
    <div style={{ minWidth: 120 }}>
      <span style={{ fontSize: 13, color: t.text, fontWeight: 600 }}>
        {nombre(valeur)}{plafond ? <span style={{ color: t.textMuted, fontWeight: 400 }}> / {nombre(plafond)}</span> : null}
      </span>
      {plafond ? (
        <div style={{ height: 5, borderRadius: 99, background: t.bgInput, marginTop: 5, overflow: 'hidden' }}>
          <div style={{ width: `${part}%`, height: '100%', background: serre ? t.warning : t.primary }} />
        </div>
      ) : null}
    </div>
  );
};

const Carte = ({ Icon, libelle, valeur, detail, t }) => (
  <div style={{ background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 14,
    padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 14 }}>
    <div style={{ width: 42, height: 42, borderRadius: 12, background: `${t.primary}15`,
      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <Icon size={20} style={{ color: t.primary }} />
    </div>
    <div>
      <p style={{ fontSize: 11, fontWeight: 600, color: t.textMuted, margin: 0,
        textTransform: 'uppercase', letterSpacing: '0.5px' }}>{libelle}</p>
      <p style={{ fontSize: 22, fontWeight: 800, color: t.text, margin: 0, lineHeight: 1.2 }}>{valeur}</p>
      {detail && <p style={{ fontSize: 12, color: t.textMuted, margin: '2px 0 0' }}>{detail}</p>}
    </div>
  </div>
);

const Activite = () => {
  const t = useTheme();
  const [clients, setClients] = useState([]);
  const [fenetre, setFenetre] = useState(30);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(null);

  const charger = useCallback(async () => {
    setChargement(true);
    setErreur(null);
    try {
      const r = await superAdminApi.activite.etat(fenetre);
      setClients(r.data?.clients || []);
    } catch (e) {
      setErreur(e.message || 'Lecture impossible');
    } finally {
      setChargement(false);
    }
  }, [fenetre]);

  useEffect(() => { charger(); }, [charger]);

  const totaux = useMemo(() => ({
    clients: clients.length,
    aSurveiller: clients.filter((c) => c.signal !== 'normal').length,
    employes: clients.reduce((s, c) => s + Number(c.employes || 0), 0),
    pointages: clients.reduce((s, c) => s + Number(c.pointages || 0), 0)
  }), [clients]);

  const periode = FENETRES.find((f) => f.valeur === fenetre)?.libelle || '';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
        <div>
          <h2 style={{ fontSize: 20, fontWeight: 800, color: t.text, margin: 0,
            display: 'flex', alignItems: 'center', gap: 9 }}>
            <LineChart size={20} style={{ color: t.primary }} />
            Activité par client
          </h2>
          <p style={{ fontSize: 13, color: t.textMuted, margin: '4px 0 0' }}>
            Ce que chaque entreprise a réellement fait sur {periode.toLowerCase()}.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <select value={fenetre} onChange={(e) => setFenetre(Number(e.target.value))}
            style={{ background: t.bgInput, border: `1px solid ${t.border}`, borderRadius: 10,
              padding: '9px 12px', fontSize: 13, color: t.text, fontFamily: 'inherit' }}>
            {FENETRES.map((f) => <option key={f.valeur} value={f.valeur}>{f.libelle}</option>)}
          </select>
          <button type="button" onClick={charger}
            style={{ background: t.bgInput, border: `1px solid ${t.border}`, borderRadius: 10,
              padding: '9px 14px', fontSize: 13, fontWeight: 600, color: t.text, cursor: 'pointer',
              display: 'inline-flex', alignItems: 'center', gap: 8, fontFamily: 'inherit' }}>
            <RefreshCw size={14} style={{ animation: chargement ? 'spin 1s linear infinite' : 'none' }} />
            Actualiser
          </button>
        </div>
      </div>

      {erreur && (
        <div style={{ background: t.dangerSoft, border: `1px solid ${t.danger}30`, borderRadius: 14,
          padding: '14px 18px', fontSize: 14, color: t.dangerText }}>{erreur}</div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 12 }}>
        <Carte Icon={Users} libelle="Clients servis" valeur={totaux.clients} t={t} />
        <Carte Icon={AlertTriangle} libelle="À regarder" valeur={totaux.aSurveiller}
          detail={totaux.aSurveiller ? 'coupés, décrochés ou au plafond' : 'aucun'} t={t} />
        <Carte Icon={Users} libelle="Employés suivis" valeur={nombre(totaux.employes)} t={t} />
        <Carte Icon={Clock} libelle="Pointages" valeur={nombre(totaux.pointages)} detail={periode} t={t} />
      </div>

      <div style={{ background: t.bgCard, border: `1px solid ${t.border}`, borderRadius: 14, overflow: 'hidden' }}>
        {chargement && !clients.length && (
          <p style={{ margin: 0, padding: 18, fontSize: 14, color: t.textMuted }}>Lecture de l&rsquo;activité…</p>
        )}

        {!!clients.length && (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: t.bgTableHeader }}>
                {['Client', 'Signal', 'Employés', 'Comptes', 'Pointages', 'Connexions',
                  'Dernière connexion', 'Licence'].map((titre) => (
                  <th key={titre} style={{ textAlign: 'left', padding: '11px 16px', fontSize: 11,
                    fontWeight: 700, color: t.textMuted, textTransform: 'uppercase',
                    letterSpacing: '0.5px', borderBottom: `1px solid ${t.border}` }}>{titre}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {clients.map((c) => (
                <tr key={c.id} style={{ borderBottom: `1px solid ${t.borderLight}` }}>
                  <td style={{ padding: '12px 16px' }}>
                    <span style={{ fontSize: 14, fontWeight: 700, color: t.text }}>{c.company_name}</span>
                    <span style={{ display: 'block', fontSize: 12, color: t.textMuted }}>
                      client depuis le {jourSeul(c.client_depuis)}
                    </span>
                  </td>
                  <td style={{ padding: '12px 16px' }}><Pastille signal={c.signal} t={t} /></td>
                  <td style={{ padding: '12px 16px' }}>
                    <Jauge valeur={c.employes} plafond={c.plafond_employes} t={t} />
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <Jauge valeur={c.utilisateurs} plafond={c.plafond_utilisateurs} t={t} />
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: 13, color: t.text }}>
                    {nombre(c.pointages)}
                    <span style={{ display: 'block', fontSize: 12, color: t.textMuted }}>
                      dernier le {jourSeul(c.dernier_pointage)}
                    </span>
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: 13, color: t.text }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <LogIn size={13} style={{ color: t.textMuted }} />
                      {nombre(c.connexions)}
                    </span>
                    <span style={{ display: 'block', fontSize: 12, color: t.textMuted }}>
                      {c.comptes_actifs} compte(s)
                      {c.connexions_refusees > 0 ? ` · ${c.connexions_refusees} refus` : ''}
                    </span>
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: 13, color: t.textSecondary, whiteSpace: 'nowrap' }}>
                    {quand(c.derniere_connexion)}
                    {c.jours_sans_connexion !== null && (
                      <span style={{ display: 'block', fontSize: 12,
                        color: c.jours_sans_connexion >= 14 ? t.warningText : t.textMuted }}>
                        il y a {c.jours_sans_connexion} j
                      </span>
                    )}
                  </td>
                  <td style={{ padding: '12px 16px', fontSize: 13, color: t.textSecondary }}>
                    {c.type_licence || '—'}
                    <span style={{ display: 'block', fontSize: 12, color: t.textMuted }}>
                      jusqu&rsquo;au {jourSeul(c.licence_jusqu_au)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <p style={{ fontSize: 12, color: t.textMuted, margin: 0, lineHeight: 1.6 }}>
        « Décroche » signale un client dont aucun compte ne s&rsquo;est connecté depuis 14 jours ;
        « Dépasse son plafond », plus d&rsquo;employés actifs que la licence n&rsquo;en autorise.
        Les connexions sont comptées depuis le 24 septembre 2026, date d&rsquo;ouverture du journal.
      </p>
    </div>
  );
};

export default Activite;
